import React, { useCallback, useDeferredValue, useEffect, useState } from "react";
import { Plus, Search, ShoppingBag } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import {
  EmptyState,
  PanelActionButton,
  SectionPanel,
  StatusPill,
} from "@/components/platform/ContentPrimitives";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/formatters";
import {
  DIRECTORY_FOCUS_OPTIONS,
  DIRECTORY_TYPE_OPTIONS,
  PLATFORM_LINKS,
  getDirectoryTypeLabel,
} from "@/lib/polenica";

export default function Marketplace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState("");
  const [selectedFocus, setSelectedFocus] = useState("");
  const [search, setSearch] = useState("");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const deferredSearch = useDeferredValue(search);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/marketplace", {
        params: {
          tipo_oferta: selectedType || undefined,
          enfoque: selectedFocus || undefined,
          search: deferredSearch || undefined,
        },
      });
      setPosts(response.data?.items || []);
    } catch (_error) {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [deferredSearch, selectedFocus, selectedType]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="platform-hero-banner rounded-[34px] p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/70">
              Directorio de confianza
            </p>
            <h1 className="mt-3 flex items-center gap-3 font-heading text-4xl font-semibold text-primary sm:text-5xl">
              <ShoppingBag className="h-10 w-10" />
              Directorio
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-mutedForeground">
              Encuentre productos, servicios y ofertas que circulan dentro de la red. Antes de
              publicar o contactar, revise la{" "}
              <a
                href={PLATFORM_LINKS.directoryConditions}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-primary underline underline-offset-4"
              >
                informacion general y condiciones de uso
              </a>
              .
            </p>
          </div>

          {user ? (
            <PanelActionButton onClick={() => navigate("/crear-publicacion")}>
              <Plus className="h-4 w-4" />
              Crear publicacion
            </PanelActionButton>
          ) : (
            <PanelActionButton tone="secondary" onClick={() => navigate("/login")}>
              Entrar para publicar
            </PanelActionButton>
          )}
        </div>

      </div>

      <SectionPanel
        title="Explorar publicaciones"
        eyebrow="Consulta publica"
        description="Filtre por tipo de oferta, enfoque asociado o palabras clave para ubicar contactos y procesos cercanos."
      >
        <div className="grid gap-4 lg:grid-cols-[260px_260px_1fr]">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-primary">Tipo</span>
            <select
              value={selectedType}
              onChange={(event) => setSelectedType(event.target.value)}
              className="h-12 w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 outline-none transition focus:border-primary/35"
            >
              <option value="">Todos</option>
              {DIRECTORY_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-primary">Enfoque asociado</span>
            <select
              value={selectedFocus}
              onChange={(event) => setSelectedFocus(event.target.value)}
              className="h-12 w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 outline-none transition focus:border-primary/35"
            >
              <option value="">Todos</option>
              {DIRECTORY_FOCUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-primary">Buscar</span>
            <div className="flex items-center gap-3 rounded-2xl border border-border bg-[#FCFCF9] px-4">
              <Search className="h-4 w-4 text-primary/60" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-12 w-full bg-transparent outline-none"
                placeholder="titulo, iniciativa, comuna, barrio..."
              />
            </div>
          </label>
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="rounded-[24px] border border-primary/10 bg-[#FAFBF7] px-6 py-12 text-center text-mutedForeground">
              Cargando publicaciones...
            </div>
          ) : !posts.length ? (
            <EmptyState
              icon={ShoppingBag}
              title="No hay publicaciones para esta consulta"
              description="Ajuste los filtros o vuelva mas tarde para revisar nuevas ofertas de la red."
            />
          ) : (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {posts.map((post) => (
                <article
                  key={post.id}
                  className="cursor-pointer overflow-hidden rounded-[28px] border border-primary/10 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-[0_20px_34px_rgba(38,61,43,0.12)]"
                  onClick={() => navigate(`/directorio/${post.id}`)}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[#EEF4E7]">
                    {post.imagenes?.[0] ? (
                      <img
                        src={post.imagenes[0]}
                        alt={post.titulo}
                        className="h-full w-full object-cover transition duration-300 hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-primary/45">
                        <ShoppingBag className="h-14 w-14" />
                      </div>
                    )}
                  </div>

                  <div className="p-5">
                    <div className="flex flex-wrap gap-2">
                      <StatusPill>{getDirectoryTypeLabel(post.tipo_oferta)}</StatusPill>
                      {post.enfoques_asociados?.[0] ? (
                        <StatusPill tone="neutral">{post.enfoques_asociados[0]}</StatusPill>
                      ) : null}
                    </div>

                    <h3 className="mt-4 font-heading text-2xl font-semibold text-primary">{post.titulo}</h3>
                    <p className="mt-2 line-clamp-3 text-sm leading-6 text-mutedForeground">
                      {post.descripcion}
                    </p>

                    {post.nombre_iniciativa ? (
                      <p className="mt-3 text-sm font-medium text-primary/80">{post.nombre_iniciativa}</p>
                    ) : null}

                    <div className="mt-5 flex items-center justify-between gap-3">
                      <div className="text-sm text-mutedForeground">
                        {[post.municipio, post.comuna, post.barrio].filter(Boolean).join(" / ") ||
                          "Sin ubicacion visible"}
                      </div>
                      <div className="text-sm font-semibold text-accent">
                        {post.tipo_valor === "intercambio"
                          ? "Intercambio"
                          : formatCurrency(post.precio)}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </SectionPanel>

      {!user ? (
        <div className="mt-6 rounded-[28px] border border-primary/10 bg-white p-6 text-sm leading-7 text-mutedForeground shadow-sm">
          Puede recorrer el directorio sin iniciar sesion. Para crear, editar o gestionar sus
          ofertas debe{" "}
          <Link to="/login" className="font-semibold text-primary underline underline-offset-4">
            ingresar con su cuenta
          </Link>
          .
        </div>
      ) : null}
    </div>
  );
}
